import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from '@/components/seop/EmptyState';
import { useToast } from '@/contexts/ToastContext';
import { useSeopMonth } from '@/hooks/useSeopMonth';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { seopService } from '@/services/seop.service';
import type {
  SeopForecastColumn,
  SeopForecastImport,
  SeopForecastItem,
  SeopForecastPreview,
  SeopUnknownPolicy,
} from '@/types/seop';
import { fmtUnits } from '@/utils/seopFormat';

type Etapa = 'upload' | 'conciliacao' | 'confirmacao';

/** `{ letra da coluna: id do mês | null }`. `null` = coluna descartada. */
type Mapeamento = Record<string, string | null>;
type Politicas = Record<string, SeopUnknownPolicy>;

const CELL = 'py-2 pr-3 text-sm text-content-primary';
const HEAD = 'py-2 pr-3 text-left text-xs font-semibold uppercase text-content-muted';

/**
 * Quantidade do forecast. `fmtUnits` arredonda para inteiro, e o rateio do comercial vem
 * fracionado (`38767,3323…`) — arredondar aqui faria duas linhas com valores diferentes
 * aparecerem iguais bem na tela cujo trabalho é mostrar o que muda.
 */
function fmtQtd(valor: number): string {
  if (!Number.isFinite(valor)) return '—';
  return Number.isInteger(valor)
    ? fmtUnits(valor)
    : valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function mapeamentoInicial(colunas: SeopForecastColumn[]): Mapeamento {
  // Só o candidato único vem pré-selecionado — mês ambíguo o usuário escolhe (B-D9).
  return Object.fromEntries(colunas.map((c) => [c.column, c.suggested_month_id]));
}

interface LinhaDiff {
  chave: string;
  code: string;
  descricao: string;
  mes: string;
  atual: number | null;
  novo: number;
}

function montarDiff(
  preview: SeopForecastPreview,
  mapeamento: Mapeamento,
  politicas: Politicas,
  atuais: Map<string, number>,
  rotuloDoMes: Map<string, string>,
): LinhaDiff[] {
  const colunas = Object.entries(mapeamento).filter(([, mesId]) => Boolean(mesId));
  const linhas: LinhaDiff[] = [];
  for (const item of preview.items) {
    if (item.status === 'unknown' && politicas[item.code] !== 'create_product') continue;
    for (const [coluna, mesId] of colunas) {
      linhas.push({
        chave: `${item.code}:${coluna}`,
        code: item.code,
        descricao: item.description,
        mes: rotuloDoMes.get(mesId as string) ?? '—',
        atual: item.product_id ? atuais.get(`${mesId}:${item.product_id}`) ?? null : null,
        novo: item.values[coluna] ?? 0,
      });
    }
  }
  return linhas;
}

const PassoUpload: FC<{ onArquivo: (f: File) => void; isBusy: boolean; erro: string | null }> = ({
  onArquivo,
  isBusy,
  erro,
}) => {
  const [arrastando, setArrastando] = useState(false);
  return (
    <div>
      <div
        data-testid="seop-import-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={(e) => {
          e.preventDefault();
          setArrastando(false);
          const arquivo = e.dataTransfer.files?.[0];
          if (arquivo) onArquivo(arquivo);
        }}
        className={`rounded-xl border-2 border-dashed p-10 text-center ${
          arrastando ? 'border-accent bg-accent/5' : 'border-theme-border bg-surface-card'
        }`}
      >
        <span className="material-symbols-outlined text-4xl text-content-muted">upload_file</span>
        <p className="mt-2 font-semibold text-content-primary">
          Arraste a planilha do forecast aqui
        </p>
        <p className="mt-1 text-sm text-content-muted">
          Só .xlsx, o arquivo original que o comercial enviou. Nada é gravado agora: você confere a
          conciliação antes de aplicar.
        </p>
        <label className="mt-4 inline-block cursor-pointer rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover">
          {isBusy ? 'Lendo a planilha…' : 'Escolher arquivo'}
          <input
            type="file"
            accept=".xlsx"
            aria-label="Planilha do forecast"
            disabled={isBusy}
            className="hidden"
            onChange={(e) => {
              const arquivo = e.target.files?.[0];
              if (arquivo) onArquivo(arquivo);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {erro && (
        <div
          data-testid="seop-import-erro"
          className="mt-4 rounded-lg border border-theme-danger/40 bg-theme-danger/10 p-4 text-sm text-content-primary"
        >
          {erro}
        </div>
      )}
    </div>
  );
};

const BlocoMeses: FC<{
  colunas: SeopForecastColumn[];
  mapeamento: Mapeamento;
  totais: Record<string, number>;
  onChange: (coluna: string, mesId: string | null) => void;
}> = ({ colunas, mapeamento, totais, onChange }) => {
  const duplicadas = colunas.filter((c) => c.duplicate_of);
  return (
    <section className="rounded-xl border border-theme-border bg-surface-card p-5">
      <h3 className="font-semibold text-content-primary">Para qual mês vai cada coluna?</h3>
      <p className="mt-1 text-sm text-content-muted">
        O mês de planejamento vai do dia 16 do mês anterior ao dia 15 do corrente — o intervalo
        aparece em cada opção. Coluna sem mês é descartada.
      </p>
      {duplicadas.length > 0 && (
        <div
          data-testid="seop-import-duplicadas"
          className="mt-3 rounded-lg border border-theme-warning/40 bg-theme-warning/10 p-3 text-sm text-content-primary"
        >
          {duplicadas.map((c) => c.header).join(', ')}{' '}
          {duplicadas.length > 1 ? 'trazem' : 'traz'} o mesmo valor de{' '}
          {colunas.find((c) => c.column === duplicadas[0].duplicate_of)?.header} — vêm do mesmo bloco
          de rateio da planilha. Confirme se é isso mesmo antes de gravar o número repetido.
        </div>
      )}
      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {colunas.map((c) => (
          <label key={c.column} className="text-xs text-content-muted">
            {c.header} <span className="font-normal">(coluna {c.column})</span>
            <select
              aria-label={`Mês da coluna ${c.header}`}
              value={mapeamento[c.column] ?? ''}
              onChange={(e) => onChange(c.column, e.target.value || null)}
              className="mt-1 w-full rounded-lg border border-theme-border bg-surface-secondary px-2 py-1.5 text-sm text-content-primary"
            >
              <option value="">Ignorar esta coluna</option>
              {c.candidates.map((cand) => (
                <option key={cand.id} value={cand.id}>
                  {cand.label} — {cand.month_name} ({cand.range})
                </option>
              ))}
            </select>
            <span className="mt-1 block text-2xs text-content-muted">
              Total na planilha: {fmtQtd(totais[c.column] ?? 0)}
            </span>
          </label>
        ))}
      </div>
    </section>
  );
};

const LinhaItem: FC<{
  item: SeopForecastItem;
  politica: SeopUnknownPolicy | undefined;
  onPolitica: (code: string, politica: SeopUnknownPolicy) => void;
}> = ({ item, politica, onPolitica }) => (
  <tr data-testid={`seop-import-item-${item.code}`} className="border-t border-theme-border">
    <td className={CELL}>{item.code}</td>
    <td className={CELL}>
      {item.description}
      {item.product_description && item.product_description !== item.description && (
        <span className="block text-xs text-content-muted">
          No cadastro: {item.product_description}
        </span>
      )}
    </td>
    <td className={CELL}>
      <span
        className={`rounded-full px-2 py-0.5 text-xs ${
          item.status === 'matched'
            ? 'bg-theme-success/15 text-theme-success'
            : 'bg-theme-warning/15 text-theme-warning'
        }`}
      >
        {item.status === 'matched' ? 'No cadastro' : 'Fora do cadastro'}
      </span>
    </td>
    <td className={CELL}>
      {item.status === 'unknown' ? (
        <select
          aria-label={`Item ${item.code} fora do cadastro`}
          value={politica ?? ''}
          onChange={(e) => onPolitica(item.code, e.target.value as SeopUnknownPolicy)}
          className="rounded-lg border border-theme-border bg-surface-secondary px-2 py-1 text-sm"
        >
          <option value="" disabled>
            Escolha…
          </option>
          <option value="ignore">Ignorar</option>
          <option value="create_product">Criar produto</option>
        </select>
      ) : (
        <span className="text-sm text-content-muted">—</span>
      )}
    </td>
  </tr>
);

const BlocoItens: FC<{
  itens: SeopForecastItem[];
  politicas: Politicas;
  onPolitica: (code: string, politica: SeopUnknownPolicy) => void;
  onTodos: (politica: SeopUnknownPolicy) => void;
}> = ({ itens, politicas, onPolitica, onTodos }) => {
  const desconhecidos = itens.filter((i) => i.status === 'unknown');
  return (
    <section className="rounded-xl border border-theme-border bg-surface-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-content-primary">
          Itens da planilha ({itens.length}) — {desconhecidos.length} fora do cadastro
        </h3>
        {desconhecidos.length > 0 && (
          <div className="flex gap-2 text-sm">
            <span className="text-content-muted">Aplicar a todos os desconhecidos:</span>
            <button type="button" className="underline" onClick={() => onTodos('ignore')}>
              Ignorar
            </button>
            <button type="button" className="underline" onClick={() => onTodos('create_product')}>
              Criar produto
            </button>
          </div>
        )}
      </div>
      <div className="mt-3 max-h-96 overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={HEAD}>Código</th>
              <th className={HEAD}>Descrição na planilha</th>
              <th className={HEAD}>Situação</th>
              <th className={HEAD}>O que fazer</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <LinhaItem
                key={item.code}
                item={item}
                politica={politicas[item.code]}
                onPolitica={onPolitica}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const PassoConfirmacao: FC<{ linhas: LinhaDiff[]; novos: string[] }> = ({ linhas, novos }) => {
  const mudam = linhas.filter((l) => l.atual === null || l.atual !== l.novo);
  return (
    <section className="rounded-xl border border-theme-border bg-surface-card p-5">
      <h3 className="font-semibold text-content-primary">O que vai mudar</h3>
      <p className="mt-1 text-sm text-content-muted">
        {mudam.length} de {linhas.length} valores mudam. O resto já está igual no plano.
      </p>
      {novos.length > 0 && (
        <div
          data-testid="seop-import-aviso-novos"
          className="mt-3 rounded-lg border border-theme-warning/40 bg-theme-warning/10 p-3 text-sm text-content-primary"
        >
          {novos.length} produto(s) serão criados só com código e descrição ({novos.join(', ')}).
          Enquanto não tiverem roteiro e parâmetro de lote, a demanda aparece mas a carga não muda —
          o cálculo pula produto sem lote. Complete o cadastro em Cadastros depois de importar.
        </div>
      )}
      <div className="mt-3 max-h-96 overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={HEAD}>Código</th>
              <th className={HEAD}>Produto</th>
              <th className={HEAD}>Mês</th>
              <th className={`${HEAD} text-right`}>Hoje</th>
              <th className={`${HEAD} text-right`}>Depois</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              const mudou = l.atual === null || l.atual !== l.novo;
              return (
                <tr key={l.chave} className="border-t border-theme-border">
                  <td className={CELL}>{l.code}</td>
                  <td className={CELL}>{l.descricao}</td>
                  <td className={CELL}>{l.mes}</td>
                  <td className={`${CELL} text-right text-content-muted`}>
                    {l.atual === null ? '—' : fmtQtd(l.atual)}
                  </td>
                  <td className={`${CELL} text-right ${mudou ? 'font-semibold text-accent' : ''}`}>
                    {fmtQtd(l.novo)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

function resumoDoImport(imp: SeopForecastImport): string {
  const s = imp.summary ?? {};
  if (imp.status !== 'APPLIED') return imp.status === 'DISCARDED' ? 'Descartado' : 'Aguardando conciliação';
  const valores = (s.demands_created ?? 0) + (s.demands_updated ?? 0);
  const criados = s.products_created ?? 0;
  const pendentes = s.pending_setup ?? [];
  const base = `${valores} valor(es) de demanda · ${criados} produto(s) criado(s)`;
  // A pendência de cadastro é o que explica "importei e a carga não mudou".
  return pendentes.length > 0 ? `${base} — pendentes de roteiro/lote: ${pendentes.join(', ')}` : base;
}

const HistoricoImports: FC<{
  imports: SeopForecastImport[];
  onRetomar: (id: string) => void;
  onDescartar: (id: string) => void;
}> = ({ imports, onRetomar, onDescartar }) => {
  if (imports.length === 0) return null;
  return (
    <section
      data-testid="seop-import-historico"
      className="mt-6 rounded-xl border border-theme-border bg-surface-card p-5"
    >
      <h3 className="font-semibold text-content-primary">Imports anteriores</h3>
      <table className="mt-3 w-full">
        <thead>
          <tr>
            <th className={HEAD}>Arquivo</th>
            <th className={HEAD}>Quem</th>
            <th className={HEAD}>Quando</th>
            <th className={HEAD}>Resultado</th>
            <th className={HEAD} />
          </tr>
        </thead>
        <tbody>
          {imports.map((imp) => (
            <tr key={imp.id} className="border-t border-theme-border">
              <td className={CELL}>{imp.filename}</td>
              <td className={CELL}>{imp.uploaded_by_name ?? '—'}</td>
              <td className={CELL}>{new Date(imp.created_at).toLocaleDateString('pt-BR')}</td>
              <td className={CELL}>{resumoDoImport(imp)}</td>
              <td className={`${CELL} text-right`}>
                {imp.status === 'PREVIEW' && (
                  <span className="flex justify-end gap-3 text-sm">
                    <button type="button" className="underline" onClick={() => onRetomar(imp.id)}>
                      Retomar
                    </button>
                    <button
                      type="button"
                      className="underline text-theme-danger"
                      onClick={() => onDescartar(imp.id)}
                    >
                      Descartar
                    </button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

const SeopImportPage: FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { month } = useSeopMonth();
  const [etapa, setEtapa] = useState<Etapa>('upload');
  const [imp, setImp] = useState<SeopForecastImport | null>(null);
  const [mapeamento, setMapeamento] = useState<Mapeamento>({});
  const [politicas, setPoliticas] = useState<Politicas>({});
  const [diff, setDiff] = useState<LinhaDiff[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [historico, setHistorico] = useState<SeopForecastImport[]>([]);

  const preview = imp?.preview ?? null;

  const carregarHistorico = useCallback(async () => {
    try {
      setHistorico(await seopService.listImports());
    } catch {
      setHistorico([]); // o histórico é apoio; a falha dele não bloqueia importar
    }
  }, []);

  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  /** Reabre um preview salvo — é para isso que o import fica gravado antes do commit. */
  const retomar = useCallback(
    async (id: string) => {
      setIsBusy(true);
      try {
        const salvo = await seopService.getImport(id);
        setImp(salvo);
        setMapeamento(mapeamentoInicial(salvo.preview?.month_columns ?? []));
        setPoliticas({});
        setEtapa('conciliacao');
      } catch {
        showToast('Não consegui reabrir este import.', 'error');
      } finally {
        setIsBusy(false);
      }
    },
    [showToast],
  );

  const descartar = useCallback(
    async (id: string) => {
      try {
        await seopService.discardImport(id);
        await carregarHistorico();
        showToast('Import descartado.', 'success');
      } catch {
        showToast('Não consegui descartar este import.', 'error');
      }
    },
    [carregarHistorico, showToast],
  );

  // Sair no meio da conciliação perde as decisões (o preview fica salvo, mas as escolhas
  // não) — o mesmo aviso das telas de edição da B05.
  useUnsavedChanges(etapa !== 'upload');

  const enviar = useCallback(
    async (arquivo: File) => {
      setIsBusy(true);
      setErro(null);
      try {
        const criado = await seopService.uploadForecast(arquivo);
        setImp(criado);
        setMapeamento(mapeamentoInicial(criado.preview?.month_columns ?? []));
        setPoliticas({});
        setEtapa('conciliacao');
      } catch (e) {
        const mensagem = mensagemDeErro(e, 'Não consegui ler a planilha.');
        setErro(mensagem);
        showToast(mensagem, 'error');
      } finally {
        setIsBusy(false);
      }
    },
    [showToast],
  );

  const irParaConfirmacao = useCallback(async () => {
    if (!preview) return;
    const mesesEscolhidos = Object.values(mapeamento).filter(Boolean) as string[];
    if (mesesEscolhidos.length === 0) {
      showToast('Escolha o mês de ao menos uma coluna.', 'error');
      return;
    }
    const pendentes = preview.items.filter((i) => i.status === 'unknown' && !politicas[i.code]);
    if (pendentes.length > 0) {
      showToast(`${pendentes.length} item(ns) fora do cadastro ainda sem decisão.`, 'error');
      return;
    }
    setIsBusy(true);
    try {
      const listas = await Promise.all(mesesEscolhidos.map((id) => seopService.getDemands(id)));
      const atuais = new Map<string, number>();
      mesesEscolhidos.forEach((id, i) =>
        listas[i].forEach((d) => atuais.set(`${id}:${d.product}`, d.quantity)),
      );
      const rotulos = new Map<string, string>();
      preview.month_columns.forEach((c) =>
        c.candidates.forEach((cand) => rotulos.set(cand.id, `${cand.label} — ${cand.month_name}`)),
      );
      setDiff(montarDiff(preview, mapeamento, politicas, atuais, rotulos));
      setEtapa('confirmacao');
    } catch {
      showToast('Não consegui ler a demanda atual para comparar.', 'error');
    } finally {
      setIsBusy(false);
    }
  }, [preview, mapeamento, politicas, showToast]);

  const aplicar = useCallback(async () => {
    if (!imp) return;
    setIsBusy(true);
    try {
      const aplicado = await seopService.commitImport(imp.id, {
        confirm: true,
        month_mapping: mapeamento,
        unknown_policy: politicas,
      });
      const resumo = aplicado.summary ?? {};
      setEtapa('upload'); // limpa o "tem edição pendente" antes de navegar
      showToast(
        `Importado: ${(resumo.demands_created ?? 0) + (resumo.demands_updated ?? 0)} valores de ` +
          `demanda, ${resumo.products_created ?? 0} produto(s) criado(s).`,
        'success',
      );
      navigate(`/balanceamento-linha/demanda?mes=${month}`);
    } catch (e) {
      showToast(mensagemDeErro(e, 'Não consegui aplicar o import.'), 'error');
    } finally {
      setIsBusy(false);
    }
  }, [imp, mapeamento, politicas, month, navigate, showToast]);

  const novosProdutos = useMemo(
    () =>
      (preview?.items ?? [])
        .filter((i) => i.status === 'unknown' && politicas[i.code] === 'create_product')
        .map((i) => i.code),
    [preview, politicas],
  );

  if (etapa === 'upload' || !preview) {
    return (
      <div className="p-6 lg:p-10">
        <h2 className="mb-1 text-lg font-semibold text-content-primary">Importar do forecast</h2>
        <p className="mb-4 text-sm text-content-muted">
          Passo 1 de 3 — a planilha que o comercial envia vira demanda por produto e mês.
        </p>
        <PassoUpload onArquivo={enviar} isBusy={isBusy} erro={erro} />
        <HistoricoImports imports={historico} onRetomar={retomar} onDescartar={descartar} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6 lg:p-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">
            {etapa === 'conciliacao' ? 'Passo 2 de 3 — conciliação' : 'Passo 3 de 3 — confirmação'}
          </h2>
          <p className="text-sm text-content-muted">
            {imp?.filename} · aba “{preview.sheet_name}” · {preview.summary.n_items} itens (
            {preview.summary.n_matched} no cadastro, {preview.summary.n_unknown} fora)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => (etapa === 'confirmacao' ? setEtapa('conciliacao') : setEtapa('upload'))}
            className="rounded-lg border border-theme-border px-4 py-2 text-sm text-content-secondary hover:text-content-primary"
          >
            Voltar
          </button>
          {etapa === 'conciliacao' ? (
            <button
              type="button"
              data-testid="seop-import-revisar"
              disabled={isBusy}
              onClick={irParaConfirmacao}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
            >
              Revisar o que vai mudar
            </button>
          ) : (
            <button
              type="button"
              data-testid="seop-import-aplicar"
              disabled={isBusy}
              onClick={aplicar}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
            >
              {isBusy ? 'Aplicando…' : 'Aplicar'}
            </button>
          )}
        </div>
      </div>

      {preview.summary.warnings.length > 0 && (
        <div className="rounded-lg border border-theme-warning/40 bg-theme-warning/10 p-3 text-sm text-content-primary">
          <strong>Avisos da leitura:</strong>
          <ul className="mt-1 list-inside list-disc">
            {preview.summary.warnings.slice(0, 5).map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {etapa === 'conciliacao' ? (
        <>
          <BlocoMeses
            colunas={preview.month_columns}
            mapeamento={mapeamento}
            totais={preview.summary.totals}
            onChange={(coluna, mesId) => setMapeamento((atual) => ({ ...atual, [coluna]: mesId }))}
          />
          <BlocoItens
            itens={preview.items}
            politicas={politicas}
            onPolitica={(code, politica) =>
              setPoliticas((atual) => ({ ...atual, [code]: politica }))
            }
            onTodos={(politica) =>
              setPoliticas((atual) => {
                const proximo = { ...atual };
                preview.items
                  .filter((i) => i.status === 'unknown')
                  .forEach((i) => {
                    proximo[i.code] = politica;
                  });
                return proximo;
              })
            }
          />
        </>
      ) : diff.length === 0 ? (
        <EmptyState
          title="Nada a importar com essas escolhas"
          hint="Volte e associe ao menos uma coluna a um mês, ou marque algum item desconhecido para ser criado."
        />
      ) : (
        <PassoConfirmacao linhas={diff} novos={novosProdutos} />
      )}
    </div>
  );
};

/** Mensagem do backend (`{error, message}`) ou o fallback. Nunca "[object Object]". */
function mensagemDeErro(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { message?: string } } })?.response?.data;
  return typeof data?.message === 'string' ? data.message : fallback;
}

export default SeopImportPage;
